import React, { useEffect, useMemo, useState } from "react";
import CountingTrumpsTrainer from "./CountingTrumpsTrainer";
import {
  generateCountingHandsPuzzles,
  normalizeMode,
  COUNTING_HANDS_MODES,
  T,
} from "./countingHandsGenerator";
import { loadRealCountDeals, buildRealPuzzles, dealToPuzzle } from "./realCountHands";
import "./CountingHandsTrainer.css";

// Counting Hands — declarer view, endless seeded random deals. Unlike the other
// trainers there is no hand-written puzzle array: a fresh batch is generated on
// every visit. Mode = how many suits you have to count (1, 2 or 3); it lives in
// the URL so a mode is shareable and switching it deals a new batch.
// See countingHandsGenerator for the deal/script/prompt logic and
// docs/counting-hands-plan.md for the feature plan. Defender view comes later.
function CountingHandsTrainer({ hideCategoryTabs = false, canUseAllModes = false, onSubscribeClick, onHandDone, ...routeProps }) {
  const search = routeProps.location?.search || "";
  const params = new URLSearchParams(search);
  const requestedMode = normalizeMode(params.get("mode"));
  // GATE (Paul, 2026-08-31): "it should be paying members only, let non paying
  // members do level 1 only." Enforced on the MODE, not just the buttons - the
  // mode travels in the query string, so gating the buttons alone would be
  // walked straight past by anyone typing ?mode=3.
  const mode = canUseAllModes ? requestedMode : 1;
  const lockedOut = requestedMode !== mode;
  const seedsParam = params.get("seeds");
  // REAL DEALS (Paul, 9 Oct 2026: "replace the existing count real hands which are randomly generated
  // hands, with that"): deals come from real records (public/data/real-count-hands.json, realCountHands.js).
  // The generator is kept only as a safety net if the deal file cannot be fetched, and for ?seeds= (debugging).
  const useGenerated = !!seedsParam;
  const dealParam = params.get("deal");
  const [realDeals, setRealDeals] = useState(null); // null = loading, [] = none (fall back to generated)
  useEffect(() => {
    if (useGenerated) return undefined;
    let alive = true;
    loadRealCountDeals().then((d) => { if (alive) setRealDeals(d); });
    return () => { alive = false; };
  }, [useGenerated]);
  // A new shuffled batch per (mode, visit); the key below remounts the trainer on a mode change.
  const [batch, setBatch] = useState(0);
  useEffect(() => { setBatch((b) => b + 1); }, [mode]);

  // One batch per (mode, seeds): revisiting the page deals a fresh set.
  // ?seeds=1,2,3 replays specific deals (debugging / sharing).
  const puzzles = useMemo(() => {
    if (!useGenerated && realDeals && realDeals.length) {
      // ?deal=<key> pins one real deal (sharing / debugging), e.g. ?deal=wbs-qf-bbo-86960:28:c
      const pinned = dealParam ? realDeals.find((d) => d.key === dealParam) : null;
      if (pinned && pinned.asks.length >= mode) return [dealToPuzzle(pinned, mode)];
      const real = buildRealPuzzles(realDeals, mode, 16);
      if (real.length) return real;
    }
    const seeds = seedsParam
      ? seedsParam.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isFinite(n))
      : null;
    return generateCountingHandsPuzzles(seeds ? seeds.length : 16, seeds, mode);
  }, [mode, seedsParam, useGenerated, realDeals, batch, dealParam]);
  const loadingReal = !useGenerated && realDeals === null;

  const selectMode = (next) => {
    if (next === mode) return;
    if (!canUseAllModes && next !== 1) {
      if (onSubscribeClick) onSubscribeClick();
      return;
    }
    const nextParams = new URLSearchParams(search);
    nextParams.set("mode", String(next));
    nextParams.delete("seeds"); // a seed list belongs to the mode it was dealt for
    routeProps.history?.push({
      pathname: routeProps.location.pathname,
      search: `?${nextParams.toString()}`,
    });
  };

  return (
    <div className="ch-page">
      <div className="ch-modeBar" role="group" aria-label="How many suits to count">
        {COUNTING_HANDS_MODES.map((m) => (
          <button
            key={m}
            type="button"
            className={`ch-modeBtn${m === mode ? " ch-modeBtn--active" : ""}`}
            aria-pressed={m === mode}
            onClick={() => selectMode(m)}
          >
            {T.modeLabel(m)}
            {!canUseAllModes && m !== 1 ? " 🔒" : ""}
          </button>
        ))}
      </div>
      {!canUseAllModes ? (
        <p className="ch-modeNote">
          {lockedOut ? T.modeLockedRedirected : T.modeLockedNote}{" "}
          <button type="button" className="ch-modeUpgrade" onClick={onSubscribeClick}>
            {T.modeUpgradeCta}
          </button>
        </p>
      ) : null}
      {loadingReal ? <p className="ch-modeNote" role="status">{T.loadingReal}</p> : null}
      {!useGenerated && !loadingReal && realDeals && realDeals.length ? <p className="ch-modeNote">{T.auctionNoteReal}</p> : null}
      {loadingReal ? null : (
      <CountingTrumpsTrainer
        // Remount on a mode change so the trainer starts the new batch at deal 1.
        key={`ch-mode-${mode}-${seedsParam || ""}-${useGenerated ? "gen" : `real${batch}`}`}
        {...routeProps}
        puzzlesOverride={puzzles}
        // A deal finished at this level (Game Plan points: level = points, once a day each).
        onPuzzleDone={onHandDone ? () => onHandDone(mode) : undefined}
        trainerLabel="Counting Hands"
        categoryKey="counting"
        // Endless generated deals have no Stage 1/2/3 ladder and no
        // hand-authored themes — the mode picker above is the only selector.
        hideDifficultyTabs
        hideThemeJump
        hideCategoryTabs={hideCategoryTabs}
        // Lives under the Treadmill tab (Paul, 2026-08-25): the Counting tab has
        // its own hand-authored problems; this endless drill belongs with the
        // Treadmill tools. categoryKey stays "counting" for the engine's
        // compass/difficulty handling — only the highlighted tab changes.
        activeCategoryKeyOverride="treadmill"
        // Table + question on one screen (Paul, 28 Sep 2026). Only this drill
        // sets it; the look lives in CountingHandsTrainer.css under .ct-onescreen.
        // Live for everyone since 29 Sep 2026 (Paul: "make it live for everyone").
        layout="oneScreen"
      />
      )}
    </div>
  );
}

export default CountingHandsTrainer;
